/**
 * NavigationIndicator Component
 *
 * Visual badge shown on navigable charts in editor mode.
 * Indicates that clicking chart elements will trigger navigation.
 *
 * Created: January 2026 (Phase 4: Chart Navigation UI)
 * @see Documents/Roadmaps/Chart_Navigation_Roadmap.md
 */

import React from 'react';
import { Badge } from '@/components/ui/badge';
import { MousePointerClick } from 'lucide-react';
import { cn } from '@/lib/utils';

interface NavigationIndicatorProps {
  /** Whether the chart has navigation enabled */
  isNavigable: boolean;
  /** Number of configured navigation mappings (optional) */
  mappingCount?: number;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Visual indicator badge for navigable charts.
 *
 * Shows a "Clickable" badge in the top-right corner when
 * chart navigation is enabled. Only renders when isNavigable is true.
 *
 * @example
 * ```tsx
 * // In a chart component
 * const { isNavigable } = useChartNavigation(component, interaction);
 *
 * return (
 *   <div className="relative">
 *     <NavigationIndicator isNavigable={isNavigable && !isPreview} />
 *     <ResponsiveContainer>...</ResponsiveContainer>
 *   </div>
 * );
 * ```
 */
export const NavigationIndicator: React.FC<NavigationIndicatorProps> = ({
  isNavigable,
  mappingCount,
  className,
}) => {
  if (!isNavigable) {
    return null;
  }

  return (
    <div className={cn('absolute top-2 right-2 z-10', className)}>
      <Badge
        variant="secondary"
        className="text-xs gap-1 px-2 py-0.5 bg-background/80 backdrop-blur-sm border shadow-sm"
      >
        <MousePointerClick className="w-3 h-3" />
        Clickable
        {mappingCount !== undefined && mappingCount > 0 && (
          <span className="text-muted-foreground">({mappingCount})</span>
        )}
      </Badge>
    </div>
  );
};

export default NavigationIndicator;
